import React, { useEffect, useRef, useState } from 'react';
import ImageUploadCropDialog from './ImageUploadCropDialog';
import {
  registerWysiwygImagePicker,
  unregisterWysiwygImagePicker,
  WysiwygImagePickerOptions,
} from '../utils/tinymceImagePicker';
import { validateImageFile } from '../utils/imageUploadCore';

/**
 * Single global host bridging TinyMCE to ImageUploadCropDialog.
 * Mount once in App.tsx.
 */
const WysiwygImagePickerHost: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const resolveRef = useRef<((url: string | null) => void) | null>(null);
  const optionsRef = useRef<WysiwygImagePickerOptions>({});
  const [cropOpen, setCropOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [activePickerOptions, setActivePickerOptions] = useState<WysiwygImagePickerOptions>({});

  const finishPicker = (url: string | null) => {
    resolveRef.current?.(url);
    resolveRef.current = null;
    optionsRef.current = {};
    setActivePickerOptions({});
  };

  useEffect(() => {
    registerWysiwygImagePicker((options) => {
      return new Promise((resolve) => {
        resolveRef.current = resolve;
        optionsRef.current = options ?? {};
        fileInputRef.current?.click();
      });
    });
    return () => unregisterWysiwygImagePicker();
  }, []);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      finishPicker(null);
      return;
    }

    const maxSize = optionsRef.current.maxSize ?? 1;
    const validationError = validateImageFile(file, maxSize);
    if (validationError) {
      window.alert(validationError);
      finishPicker(null);
      return;
    }

    setActivePickerOptions({ ...optionsRef.current });
    setSelectedFile(file);
    setCropOpen(true);
  };

  const handleCropClose = () => {
    setCropOpen(false);
    setSelectedFile(null);
    finishPicker(null);
  };

  const handleUploaded = (imageUrl: string) => {
    setCropOpen(false);
    setSelectedFile(null);
    finishPicker(imageUrl);
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      <ImageUploadCropDialog
        open={cropOpen}
        file={selectedFile}
        onClose={handleCropClose}
        onUploaded={handleUploaded}
        maxSize={activePickerOptions.maxSize}
        forcedAspectRatio={activePickerOptions.forcedAspectRatio}
        hideAspectRatioSelector={activePickerOptions.hideAspectRatioSelector}
      />
    </>
  );
};

export default WysiwygImagePickerHost;
